import { useState } from 'react';
import { apiPost, ApiError } from '../../lib/api';
import { ToolGate } from '../../components/ToolGate';
import { Button, Alert, Spinner, EmptyState } from '../../components/ui';

// ── Types ────────────────────────────────────────────────────────────────────

type ContentKind = 'brand_voice' | 'calendar';

interface ContentResponse {
  kind: ContentKind;
  markdown: string;
}

interface OptionCard {
  kind: ContentKind;
  label: string;
  description: string;
}

// ── Constants ────────────────────────────────────────────────────────────────

const OPTIONS: OptionCard[] = [
  {
    kind: 'brand_voice',
    label: 'Brand voice playbook',
    description: 'A reusable guide to your tone, style, and messaging pillars.',
  },
  {
    kind: 'calendar',
    label: '30-day content calendar',
    description: 'A full month of post ideas mapped to your audience and goals.',
  },
];

function kindLabel(kind: ContentKind): string {
  return kind === 'brand_voice' ? 'Brand voice playbook' : '30-day content calendar';
}

// ── Component ────────────────────────────────────────────────────────────────

export default function Content() {
  const [loading, setLoading] = useState<ContentKind | null>(null);
  const [result, setResult] = useState<ContentResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  // ── Handlers ──────────────────────────────────────────────────────────────

  async function handleGenerate(kind: ContentKind) {
    setLoading(kind);
    setError(null);
    setResult(null);
    setCopied(false);

    try {
      const res = await apiPost<ContentResponse>('/api/tools/content', { kind });
      setResult(res);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('Something went wrong.');
      }
    } finally {
      setLoading(null);
    }
  }

  async function handleCopy() {
    if (!result) return;
    await navigator.clipboard.writeText(result.markdown);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  function handleRegenerate() {
    if (!result) return;
    void handleGenerate(result.kind);
  }

  const busy = loading !== null;

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <ToolGate tool="content" title="Content">
      <div className="mx-auto max-w-2xl space-y-8 px-4 py-10">

        {/* Heading */}
        <div>
          <h1 className="h-display text-3xl text-soft">Content</h1>
          <p className="mt-1 text-sm text-muted">
            Generate a brand-voice playbook or a 30-day content calendar in your voice.
          </p>
        </div>

        {/* Option cards */}
        <div className="grid gap-4 sm:grid-cols-2">
          {OPTIONS.map((opt) => {
            const isActive = loading === opt.kind;
            return (
              <button
                key={opt.kind}
                onClick={() => void handleGenerate(opt.kind)}
                disabled={busy}
                className={[
                  'card flex flex-col gap-2 p-5 text-left transition-colors',
                  'hover:border-electric/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-electric',
                  busy ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
                ].join(' ')}
              >
                <span className="font-head text-base font-semibold text-soft">{opt.label}</span>
                <span className="text-sm text-muted">{opt.description}</span>
                {isActive && (
                  <span className="mt-1 flex items-center gap-2 text-xs text-electric">
                    <Spinner className="h-3.5 w-3.5" />
                    Writing your {opt.label.toLowerCase()}…
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Global spinner label when busy (screen-reader accessible, visually handled in cards) */}
        {busy && (
          <p className="sr-only" aria-live="polite">
            Writing your {kindLabel(loading!).toLowerCase()}, please wait…
          </p>
        )}

        {/* Error */}
        {error && <Alert kind="error">{error}</Alert>}

        {/* Result */}
        {result ? (
          <div className="card space-y-4 p-5">
            {/* Result header */}
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h2 className="font-head text-base font-semibold text-soft">
                {kindLabel(result.kind)}
              </h2>
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  className="text-xs"
                  onClick={() => void handleCopy()}
                  disabled={busy}
                >
                  {copied ? 'Copied' : 'Copy'}
                </Button>
                <Button
                  variant="ghost"
                  className="text-xs"
                  onClick={handleRegenerate}
                  disabled={busy}
                  loading={busy}
                >
                  Regenerate
                </Button>
              </div>
            </div>

            {/* Markdown output */}
            <pre className="whitespace-pre-wrap rounded-xl bg-surface-2 p-4 font-sans text-sm leading-relaxed text-soft">
              {result.markdown}
            </pre>
          </div>
        ) : (
          !busy && !error && (
            <EmptyState
              title="Nothing generated yet"
              body="Pick one above — it's built from your business profile and brand voice from onboarding."
            />
          )
        )}
      </div>
    </ToolGate>
  );
}
